import { token } from '@atlaskit/tokens';
import { cssMap } from '@compiled/react';
import { type BackendMessageBody, createPlacement } from '@post-office/placement-common-v2';
import { placementMiddlewareWithCacheControlNav } from '@post-office/placement-common-v2/middlewares/custom-headers';
import {
	type CustomRenderer,
	type DefaultMessagesResponse,
} from '@post-office/placement-common-v2/types';
import type { SerializableRecord } from '@post-office/serializable-record';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

const styles = cssMap({
	root: {
		color: token('color.text.subtlest'),
		font: token('font.heading.xxsmall'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.075'),
	},
});

export type PlacementRenderProps = { dismiss: () => void };

type placementDefinition = CustomRenderer<PlacementRenderProps>;

type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse | undefined;
	onMessageCountChange?: (numberOfMessages: number) => void;
};

export const { createFrontend, createBackend } = createPlacement<placementDefinition>({
	placementId: 'nav-4-projects-section',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});

// Turn off sorting based on https://atlassian.slack.com/archives/C05SKG8KUAG/p1754448904308239?thread_ts=1754448426.227599&cid=C05SKG8KUAG
export const PlacementBackend = createBackend({
	customMiddleware: placementMiddlewareWithCacheControlNav,
	params: {
		sortingMethod: undefined,
		dataRetrivalType: 'implicit-only',
	},
});

export const PlacementFrontend = createFrontend<PlacementFrontendProps>(
	(Messages) =>
		({ onMessageCountChange, preloadedInitialData }) => {
			const [{ messages, status }, setMessagesState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: preloadedInitialData?.messages,
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'nav-4-projects-section',
				},
			});

			useEffect(() => {
				if (typeof messages === 'undefined') {
					return;
				}

				if (!onMessageCountChange) {
					return;
				}

				onMessageCountChange(messages.length);
			}, [messages, onMessageCountChange]);

			const handleDismiss = useCallback(
				(messageInstanceId: string): void => {
					setMessagesState(() => {
						if (!messages) {
							return { messages: undefined, status: 'done' };
						}

						const filteredMessages = messages.filter(
							(message) => messageInstanceId !== message.messageInstanceId,
						);

						return {
							messages: filteredMessages,
							status: 'done',
						};
					});
				},
				[messages],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessagesState({
						messages: data?.messages,
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			return Array.isArray(messages) && messages.length > 0 ? (
				<div role="list">
					<div role="group" aria-labelledby="nav-4-projects-section-menu-section">
						<h2 css={styles.root} id="nav-4-projects-section-menu-section-heading">
							<FormattedMessage
								id="post-office.nav-4-projects-recommendations.section-heading"
								defaultMessage="Recommended"
								description="heading for nav4 projects recommendations section"
							/>
						</h2>
						<div role="listitem">
							{messages?.map((props, index) => (
								<Messages.render
									key={`${props.messageInstanceId}-${index}`}
									dismiss={() => handleDismiss(props.messageInstanceId)}
									{...props}
								/>
							))}
						</div>
					</div>
				</div>
			) : null;
		},
);
